import { useState, useEffect, useMemo } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS, TIPOS_USUARIO_CONOCIDOS } from '@core/lib/constants'
import { useAuth } from '@core/contexts/AuthContext'
import { getAllModules } from '@modules/index'
import { getModulosVisibles } from '@core/lib/permisos'

/**
 * Hook que devuelve los IDs de app-módulos visibles en el sidebar para el
 * usuario actual.
 *
 * FUENTE DE VERDAD: el ROL del usuario (#58 — "el Rol es la Vista").
 * Se retiró el sistema de Vistas / vistaSlug.
 *
 * Cómo se deriva:
 *   1. `getModulosVisibles(userDoc)` (lib/permisos) resuelve el rol con su
 *      cascada `extiende` y devuelve los permiso-módulos donde el usuario
 *      tiene acción 'ver'.
 *   2. Cada app-módulo declara en su config.js `permisoModulos: string[]`.
 *      El app-módulo se muestra si el usuario tiene 'ver' en al menos uno.
 *   3. `portal` (config.soloCliente) se muestra solo si tipo === 'cliente'.
 *   4. Para clientes se aplican además las capas de empresa:
 *      modulosDisponibles (plataforma) y modulosActivados (toggles por empresa).
 *
 * La simulación de rol (RoleSwitcher) se resuelve en AuthContext: cuando hay
 * un rol simulado, el `userDoc` que entrega useAuth() ya viene con el rolId
 * simulado — así que este hook no necesita saber nada de la simulación.
 *
 * Returns: { modulosVisibles: string[], loading: boolean }
 */
export function useModulosVisibles() {
  const { userDoc } = useAuth()
  const allModules = useMemo(() => getAllModules(), [])

  // ── Capa 1 — permiso-módulos del rol (donde el usuario tiene 'ver') ──
  // null = todavía resolviendo; [] = sin permisos.
  const [permModulos, setPermModulos] = useState(null)

  useEffect(() => {
    let cancel = false
    if (!userDoc) { setPermModulos([]); return }

    // Fail-closed ANTES de resolver, no después. El gate de `tipo` desconocido
    // vive más abajo y siempre cerró bien, pero el efecto igual disparaba
    // `getModulosVisibles`, que pega a Firestore (rol + permisos + plan). O sea:
    // una cuenta cuyo tipo no reconocemos no entraba, pero costaba la cascada
    // de lecturas en cada login.
    //
    // Y peor que el costo: el test que decía cubrir esto —"el tipo desconocido
    // no dispara la resolución de permisos del rol"— sólo miraba que la lista
    // saliera vacía, nunca que la función no se llamara. Pasaba en verde
    // afirmando algo que el hook no hacía. Lo cazó la fiscalía del adversarial
    // sobre el commit 10250595.
    if (!TIPOS_USUARIO_CONOCIDOS.includes(userDoc.tipo || null)) { setPermModulos([]); return }

    setPermModulos(null)
    getModulosVisibles(userDoc)
      .then(codes => { if (!cancel) setPermModulos(Array.isArray(codes) ? codes : []) })
      .catch(err => {
        console.warn('useModulosVisibles: error resolviendo permisos del rol', err)
        if (!cancel) setPermModulos([])
      })
    return () => { cancel = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDoc?.id, userDoc?.tipo, userDoc?.rolId, userDoc?.rolPlataformaId, userDoc?.esWizard])

  // app-módulos derivados del rol (sin `portal` — portal es soloCliente).
  const modulosPorRol = useMemo(() => {
    if (permModulos == null) return null  // loading
    const codes = new Set(permModulos)
    return allModules
      .filter(m => !m.soloCliente && (m.permisoModulos || []).some(c => codes.has(c)))
      .map(m => m.id)
  }, [permModulos, allModules])

  // ── Tipo de usuario — TRES estados, no dos (21-ago-2026) ──
  // Antes: `(userDoc?.tipo || 'milbrands') === 'cliente'`. Ese default silencioso
  // hacía que una cuenta SIN `tipo` se tratara como staff y viera todos los
  // módulos de su rol — el peor lado para equivocarse. Con el Club llegan altas
  // que hoy no existen (el candidato externo de la bolsa), así que el gate deja
  // de ser binario: staff · cliente · **tipo desconocido ⇒ no ve nada**.
  // Ver ADR 2026-08-21-identidad-de-persona-el-usuario-es-la-persona.
  const tipoUsuario = userDoc?.tipo || null
  const tipoConocido = TIPOS_USUARIO_CONOCIDOS.includes(tipoUsuario)
  const esCliente = tipoUsuario === 'cliente'

  // ── Empresa (capas 2-3, solo clientes) ──
  const empresaId = (esCliente && userDoc?.empresaIds?.[0]) || null
  const [empresaData, setEmpresaData] = useState(null)
  const [loadingEmpresa, setLoadingEmpresa] = useState(false)

  useEffect(() => {
    if (!empresaId) { setEmpresaData(null); setLoadingEmpresa(false); return }
    setLoadingEmpresa(true)
    const unsub = onSnapshot(
      doc(db, COLLECTIONS.EMPRESAS, empresaId),
      (snap) => {
        if (snap.exists()) setEmpresaData(snap.data())
        setLoadingEmpresa(false)
      },
      (err) => {
        console.error('useModulosVisibles: error loading empresa', err)
        setLoadingEmpresa(false)
      }
    )
    return unsub
  }, [empresaId])

  // ── Resultado final ──
  const modulosVisibles = useMemo(() => {
    if (modulosPorRol == null) return []  // loading

    // Fail-closed: tipo ausente o desconocido no ve NADA. No es lo mismo que
    // "todavía no cargó" (eso ya se devolvió arriba): acá el usuario cargó y su
    // tipo no está en la lista conocida, así que no hay a qué asimilarlo.
    if (!tipoConocido) return []

    // Staff (Milbrands / consultor): directo del rol.
    if (!esCliente) return modulosPorRol

    // Cliente: módulos del rol + `portal`, luego capas de empresa.
    const base = modulosPorRol.includes('portal')
      ? modulosPorRol
      : [...modulosPorRol, 'portal']

    if (!empresaId || !empresaData) return base

    const disponibles = empresaData.modulosDisponibles
    const activados = empresaData.modulosActivados

    // Capa 2 — modulosDisponibles de la empresa. Portal siempre activo, igual
    // que en la capa 3 (report TATXKucFAz2jJv7Df1lE): ningún plan declara
    // `portal` (`@core/lib/planes.js`, PLANES_DEFAULT), así que sin esta
    // exención un tenant con `modulosDisponibles` poblado pierde sus propias
    // pantallas — y `App.jsx` sólo registra las rutas de los módulos visibles,
    // o sea 404 en `/mi-empresa`, `/mi-equipo`, `/mis-accionables`,
    // `/mis-reportes` y `/mi-diagnostico`. Se ve recién con el alta
    // self-service porque es el primer camino que SIEMPRE escribe la lista
    // (`functions/empresas/formaTenant.js`: rechaza la vacía a propósito); las
    // empresas creadas a mano suelen no tener el campo y caen en la rama
    // permisiva de acá abajo, que nunca ejercita este filtro.
    const trasDisponibles =
      (!disponibles || disponibles.length === 0)
        ? base
        : base.filter(id => id === 'portal' || disponibles.includes(id))

    // Capa 3 — modulosActivados (toggles por empresa). Portal siempre activo.
    if (!activados || activados.length === 0) return trasDisponibles
    return trasDisponibles.filter(id => id === 'portal' || activados.includes(id))
  }, [esCliente, tipoConocido, modulosPorRol, empresaId, empresaData])

  const loading = permModulos == null || (esCliente && !!empresaId && loadingEmpresa)

  // #87 — Set de permiso-módulos del rol (donde tiene 'ver'). Layout lo usa
  // para filtrar menuItems item-by-item (cada item declara `permisoModulo`).
  // Wizard salta el filtro (no tiene rolId con permisos, lo decide AuthContext).
  const permModulosSet = useMemo(
    () => new Set(permModulos || []),
    [permModulos]
  )

  return { modulosVisibles, loading, permModulosSet }
}
